import PropTypes from "prop-types";
import { useEffect, useMemo } from "react";

import { Controller, useFormContext } from "react-hook-form";

import { Paper } from "@mui/material";
import Grid from "@mui/material/Grid";

import {
  AllOutletsSelect,
  OutletRoleEnum,
  UserOutletsSelect,
} from "@/features/outlets";
import { ReturnableTypeEnum, ReturnableTypeLabel } from "../../returnable-type";
import { SupplierSelect } from "@/features/suppliers";
import {
  CustomDatePicker,
  CustomInputField,
  CustomSelect,
} from "@/features/ui";

export const GeneralInformationSection = ({ editMode = false }) => {
  const { control, watch, setValue, register, formState: { errors } } = useFormContext();

  const seller = watch("seller");
  const buyer = watch("buyer");
  const sellerableType = watch("sellerableType");
  const returnableProductVariants = watch("returnableProductVariants");
  const returnableType = watch("returnableType");
  const purchaseReturnExpenses = watch("purchaseReturnExpenses");

  // Disable buyer and seller when there are existing products
  // BUT only for GOODS_RECEIPT and PURCHASE_INVOICE modes
  // For NON_INVOICE, users manually add products so buyer/seller should remain editable
  const disableBuyerSeller =
    returnableProductVariants.length > 0 &&
    returnableType !== ReturnableTypeEnum.NON_INVOICE;

  // Return source options
  const returnableTypeOptions = useMemo(
    () =>
      Object.values(ReturnableTypeEnum).map((type) => ({
        value: type,
        label: ReturnableTypeLabel[type],
      })),
    []
  );

  // Auto-set sellerableType based on buyer's role
  useEffect(() => {
    if (buyer) {
      const sellerableType =
        buyer.role === OutletRoleEnum.HUB ? "SUPPLIER" : "OUTLET";
      setValue("sellerableType", sellerableType);
    }
  }, [buyer, setValue]);

  useEffect(() => {
    if (editMode) return;

    if (sellerableType) {
      setValue("seller", null);
    }
  }, [sellerableType, editMode, setValue]);

  useEffect(() => {
    if (seller?.supplierTaxInformation) {
      setValue("taxable", seller.supplierTaxInformation.defaultTaxIncluded);
    }
  }, [seller, setValue]);

  return (
    <Paper sx={{ p: 2.5, borderRadius: 2 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, lg: 5 }}>
          <Controller
            control={control}
            name="seller"
            render={({ field, fieldState }) =>
              sellerableType === "OUTLET" ? (
                <AllOutletsSelect
                  value={field.value}
                  onChange={field.onChange}
                  allowedRoles={[OutletRoleEnum.HUB]}
                  disabled={editMode || disableBuyerSeller || !sellerableType}
                  TextFieldProps={{
                    label: "Pemasok (Hub)",
                    required: true,
                    error: fieldState.invalid,
                    helperText: fieldState.error?.message,
                  }}
                />
              ) : (
                <SupplierSelect
                  withSupplierTaxInformation={true}
                  value={field.value}
                  onChange={(val) => {
                    field.onChange(val);
                    setValue("supplierBankAccount", null);
                  }}
                  disabled={editMode || disableBuyerSeller || !sellerableType}
                  TextFieldProps={{
                    label: "Pemasok (Eksternal)",
                    required: true,
                    error: fieldState.invalid,
                    helperText: fieldState.error?.message,
                  }}
                />
              )
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Controller
            control={control}
            name="buyer"
            render={({ field, fieldState }) => (
              <UserOutletsSelect
                value={field.value}
                onChange={field.onChange}
                disabled={editMode || disableBuyerSeller}
                enableChangeOutlet={true}
                TextFieldProps={{
                  label: "Outlet",
                  required: true,
                  error: fieldState.invalid,
                  helperText: fieldState.error?.message,
                }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <Controller
            name="transactionDate"
            control={control}
            render={({ field, fieldState }) => (
              <CustomDatePicker
                value={field.value}
                onChange={field.onChange}
                required
                label="Tanggal Transaksi"
                error={fieldState.error}
                helperText={fieldState.error?.message}
                InputProps={{
                  disableFuture: true,
                }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            control={control}
            name="returnableType"
            render={({ field, fieldState }) => (
              <CustomSelect
                value={field.value}
                onChange={(event) => {
                  const newValue = event.target.value;
                  field.onChange(newValue);
                  // Expenses reference the source invoice's own lines, so they
                  // cannot survive a change of source.
                  setValue("purchaseReturnExpenses", []);
                  setValue("returnableReferenceNumber", null);
                  if (newValue === ReturnableTypeEnum.NON_INVOICE) {
                    setValue("returnableId", null);
                  } else {
                    setValue("warehouse", null);
                  }
                }}
                options={returnableTypeOptions}
                label="Sumber Retur"
                required
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                disabled={
                  returnableProductVariants.length > 0 ||
                  purchaseReturnExpenses.length > 0
                }
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <CustomInputField
            name="note"
            label="Catatan"
            register={register}
            error={Boolean(errors.note)}
            helperText={errors.note?.message}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

GeneralInformationSection.propTypes = {
  editMode: PropTypes.bool,
};
